import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
    AvailableTimesResponse, BookingCheckoutRequest, BookingCheckoutResponse, DisabledDatesResponse,
    ProfessionalCategoryServicesResponse, ProfessionalServiceItemResponse
} from '@interfaces/professional-booking';
import { ServiceBookingApi } from './service-booking-api';


@Injectable({
    providedIn: 'root'
})
export class ProfessionalsService {

    constructor(private http: HttpClient) { }

    getProfessional(slug, params?): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/professionals/${slug}`, { params });
    }

    /** A professional service as an item (booking plan §1.1); `serviceSlug` may also be the service `_id`. */
    getService(professionalSlug: string, serviceSlug: string, preview?: boolean): Observable<ProfessionalServiceItemResponse> {
        return this.http.get<ProfessionalServiceItemResponse>(
            `${environment.apiUrl}/professionals/${professionalSlug}/${serviceSlug}`,
            { params: preview ? { preview: 'true' } : {} }
        );
    }

    getCategoryServices(categorySlug: string, params: { page?: number, limit?: number, sort?: string }):
        Observable<ProfessionalCategoryServicesResponse> {
        return this.http.get<ProfessionalCategoryServicesResponse>(`${environment.apiUrl}/professionals/category/${categorySlug}`, {
            params: { ...params }
        });
    }

    getDisabledDates(serviceId: string, start: string, end: string): Observable<DisabledDatesResponse> {
        return this.http.post<DisabledDatesResponse>(`${environment.apiUrl}/professionals/get-disabled-dates/${serviceId}`, { start, end });
    }

    getAvailableTimes(serviceId: string, date: string): Observable<AvailableTimesResponse> {
        return this.http.post<AvailableTimesResponse>(`${environment.apiUrl}/professionals/get-available-times/${serviceId}`, { date });
    }

    getProfessionals(params): Observable<any[]> {
        return this.http.get<any[]>(`${environment.apiUrl}/professionals`, { params });
    }

    register(params): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/professionals/register`, params);
    }

    requestUpgrade(params): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/professionals/upgrade`, params);
    }

    submitReview(professionalSlug, review): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/professionals/reviews/${professionalSlug}`, review);
    }

    getgallery(params): Observable<any[]> {
        return this.http.get<any[]>(`${environment.apiUrl}/professionals/gallery`, { params });
    }

    /** One booking item to the work-order checkout; `placeOrder: false` returns a priced preview. */
    checkout(params: BookingCheckoutRequest): Observable<BookingCheckoutResponse> {
        return this.http.post<BookingCheckoutResponse>(`${environment.apiUrl}/work-orders/checkout`, params);
    }
}

/** The booking pickers' endpoints for a professional service; provided by the professional service page. */
@Injectable()
export class ProfessionalBookingApiService implements ServiceBookingApi {
    readonly singleSlot = true;

    constructor(private professionalsService: ProfessionalsService) { }

    getDisabledDates(serviceId: string, start: string, end: string): Observable<DisabledDatesResponse> {
        return this.professionalsService.getDisabledDates(serviceId, start, end);
    }

    getAvailableTimes(serviceId: string, date: string): Observable<AvailableTimesResponse> {
        return this.professionalsService.getAvailableTimes(serviceId, date);
    }
}
